import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import type { DehydratedState } from '@tanstack/react-query';
import { type PersistedClient, removeOldestQuery } from '@tanstack/react-query-persist-client';
import { PERSIST_CACHE_KEY } from './query-cache';

/**
 * A cópia do painel que fica no navegador (localStorage) — só pra o F5 abrir na
 * hora, com a última tela, enquanto o painel confere com o servidor.
 *
 * ## O defeito que isto fecha
 *
 * O localStorage tem teto por site (~5 MB no Chrome). Quando a cópia passava
 * dele, `setItem` dava `QuotaExceededError` e o persister do TanStack, sem
 * `retry`, desistia CALADO: nenhum log, e o arquivo parava na última gravação
 * boa enquanto o painel seguia só na memória. O F5 seguinte abria com a foto de
 * horas atrás (caso real: "o que mudou desde 16:12" num F5 das 18:02).
 * Reproduzido no srv03 em 03/nov enchendo o espaço artificialmente; e depois do
 * F5 voltava a gravar — ciclo trava calado → F5 → grava → cresce.
 *
 * ## O que muda
 *
 * 1. Só o ARQUIVO fica menor; a memória (o que está na tela) não é tocada —
 *    a redução monta objetos novos, nunca altera os do cache:
 *    - listas (`['tickets', …]`): só a 1ª página, com o total (contador da
 *      aba) e o cursor pra continuar rolando — `pages` e `pageParams` cortados
 *      juntos. Era o que mais pesava (~50 mil caracteres por página de
 *      Fechados) e o F5 rebuscava todas as páginas guardadas;
 *    - conversas (`['ticket-messages', id]`): só as `CONVERSAS_GUARDADAS`
 *      ATUALIZADAS mais recentemente (`dataUpdatedAt` — inclui as que só
 *      receberam mensagem ao vivo, não necessariamente as abertas por último).
 *      A de fora, depois do F5, abre em "carregando" e vem do servidor.
 * 2. Mesmo assim estourou: `removeOldestQuery` tira a query de `dataUpdatedAt`
 *    mais antigo e tenta de novo, até caber.
 * 3. Nem vazia coube (espaço tomado por outra coisa): apaga a cópia salva — o
 *    próximo F5 abre limpo e busca do servidor, em vez de abrir uma foto velha
 *    se passando por atual.
 * 4. Registro mínimo: um aviso no console por carga de página, só com números.
 *    Nada de conteúdo, nada mandado ao servidor.
 */
export const CONVERSAS_GUARDADAS = 20;

type QueryDesidratada = DehydratedState['queries'][number];

function soPrimeiraPagina(q: QueryDesidratada): QueryDesidratada {
  const dados = q.state.data as { pages?: unknown[]; pageParams?: unknown[] } | undefined;
  if (!Array.isArray(dados?.pages) || !Array.isArray(dados?.pageParams)) return q;
  if (dados.pages.length <= 1) return q;
  return {
    ...q,
    state: {
      ...q.state,
      data: { ...dados, pages: dados.pages.slice(0, 1), pageParams: dados.pageParams.slice(0, 1) },
    },
  };
}

/** A cópia que vai pro arquivo — objetos novos; a entrada não é alterada. */
export function reduzirCopia(cliente: PersistedClient): PersistedClient {
  const queries = cliente.clientState.queries;
  const conversasGuardadas = new Set(
    queries
      .filter((q) => q.queryKey[0] === 'ticket-messages')
      .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)
      .slice(0, CONVERSAS_GUARDADAS),
  );
  const reduzidas: QueryDesidratada[] = [];
  for (const q of queries) {
    if (q.queryKey[0] === 'ticket-messages' && !conversasGuardadas.has(q)) continue;
    reduzidas.push(q.queryKey[0] === 'tickets' ? soPrimeiraPagina(q) : q);
  }
  // Espalha `cliente` primeiro: mantém `buster` e `timestamp` no começo do
  // texto — `prepararCopiaDoCacheNaEntrada` lê o carimbo dos primeiros bytes.
  return { ...cliente, clientState: { ...cliente.clientState, queries: reduzidas } };
}

let avisouNestaCarga = false;

/** Volta ao estado de página recém-carregada (testes). */
export function reiniciarAvisoDaCopia(): void {
  avisouNestaCarga = false;
}

export function criarPersistidorDoNavegador(
  storage: Storage | undefined,
  avisar: (msg: string, dados: Record<string, number | boolean>) => void = console.warn,
  intervaloEntreGravacoesMs = 1000,
) {
  let descartadas = 0;
  let desistiu = false;
  let avisoAgendado = false;

  const avisarUmaVez = (tamanho: number): void => {
    if (avisoAgendado) return;
    avisoAgendado = true;
    // Depois da volta síncrona de tentativas do persister: aí já se sabe se
    // coube (com quantas a menos) ou se a cópia foi apagada.
    queueMicrotask(() => {
      avisoAgendado = false;
      if (!avisouNestaCarga) {
        avisouNestaCarga = true;
        avisar('[cópia do navegador] não coube no espaço do navegador', {
          tamanho_caracteres: tamanho,
          copias_descartadas: descartadas,
          copia_apagada: desistiu,
        });
      }
      descartadas = 0;
      desistiu = false;
    });
  };

  return createSyncStoragePersister({
    storage,
    key: PERSIST_CACHE_KEY,
    throttleTime: intervaloEntreGravacoesMs,
    serialize: (cliente) => JSON.stringify(reduzirCopia(cliente)),
    retry: ({ persistedClient, error, errorCount }) => {
      if (errorCount === 1) {
        avisarUmaVez(JSON.stringify(reduzirCopia(persistedClient)).length);
      }
      const menor = removeOldestQuery({ persistedClient, error, errorCount });
      if (menor) {
        descartadas++;
        return menor;
      }
      desistiu = true;
      try {
        storage?.removeItem(PERSIST_CACHE_KEY);
      } catch {
        // Sem como apagar: o próximo F5 cai no preparo da entrada (marca recua).
      }
      return undefined;
    },
  });
}
